import {
  configKeys,
  ConfigResponseType,
  CONFIGS,
  getConfigEntries,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import { UsersSearchResponseType } from '@buildwise/libs/home/components/request-post/RequestPostModal';
import { JAVA_SERVICE_ENDPOINTS } from '@buildwise/libs/services';
import { Box, FormControl, FormHelperText } from '@mui/material';
import { AxiosResponse } from 'axios';
import Editor, { initConfig } from 'components/common/editor/Editor';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import useUserData from 'hooks/useUserData';
import { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';

type DescriptionProps = {
  autoFocus?: boolean;
  defaultHeight?: number;
  placeholder?: string;
};

function Description({ autoFocus = false, defaultHeight, placeholder }: DescriptionProps) {
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<PostFormData>();
  const queryClient = useQueryClient();
  const { companyId } = useUserData();
  useConfigQuery({
    companyId,
    configName: CONFIGS.HASHTAGS.configName,
  });
  useSearchItemQuery<UsersSearchResponseType>(
    {
      searchType: 'users',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    },
    true,
  );

  const getHashtags = useCallback(async () => {
    const queryData = queryClient.getQueryData<AxiosResponse<ConfigResponseType>>(
      configKeys.getConfig({ companyId, configName: CONFIGS.HASHTAGS.configName }),
    );

    return getConfigEntries({ data: queryData?.data }).map((entry) => entry.entryCode);
  }, [companyId]);

  const getUsers = useCallback(async () => {
    const queryData = queryClient.getQueryData<AxiosResponse<UsersSearchResponseType>>([
      JAVA_SERVICE_ENDPOINTS.SEARCH_ITEM,
      'users',
    ]);

    return (
      queryData?.data?.userResponse?.map((user) => ({
        ...user,
        name: user.userNameToDisplay,
        id: user.username,
        description: user.title,
        image: user?.profileImageURL || 'https://images.buildwise.ai/user_profile_avatar.png',
      })) || []
    );
  }, []);

  return (
    <Box position="relative" mb={1}>
      <Controller
        control={control}
        render={({ field: { ref, onChange, ...field }, fieldState }) => (
          <FormControl fullWidth>
            <Editor
              {...field}
              onEditorChange={onChange}
              onInit={(_, editor) => {
                ref(editor);
                editor.editorContainer.style.transition = 'height 0.3s';
              }}
              value={!!field?.value?.trim() ? field.value : ''}
              disabled={isSubmitting}
              init={{
                placeholder: placeholder || 'Add a description',
                min_height: defaultHeight || 150,
                height: defaultHeight || 150,
                statusbar: false,
                max_height: 300,
                auto_focus: autoFocus ? true : undefined,
                toolbar: false,
                plugins: [...(initConfig()?.plugins || []), 'autoresize', 'emoticons'],
              }}
              onBlur={() => {
                field.onBlur();
                setValue('objectDescription', field?.value?.trim() || '');
              }}
              getHashtags={getHashtags}
              getUsers={getUsers}
            />
            <FormHelperText error>{fieldState.error?.message}</FormHelperText>
          </FormControl>
        )}
        name="objectDescription"
      />
    </Box>
  );
}

export default Description;
